/**
 * Bug Report Manager - Collects diagnostics and submits via Faro
 */
export class BugReportManager {
    /**
     * @param {Object} faro - Faro instance
     * @param {Object} game - Game instance for state access
     * @param {Object} consoleCapture - ConsoleCapture instance for logs
     */
    constructor(faro, game, consoleCapture) {
        this.faro = faro;
        this.game = game;
        this.consoleCapture = consoleCapture;
        this.modal = document.getElementById('bug-report-modal');
        this.messageInput = document.getElementById('bug-report-message');
        this.technicalPre = document.getElementById('bug-report-technical');
        this.feedback = document.getElementById('bug-report-feedback');

        this.setupEventListeners();
    }

    /**
     * Set up modal event listeners
     */
    setupEventListeners() {
        const submitBtn = document.getElementById('bug-report-submit');
        const cancelBtn = document.getElementById('bug-report-cancel');

        if (submitBtn) {
            submitBtn.addEventListener('click', () => this.handleSubmit());
        }
        if (cancelBtn) {
            cancelBtn.addEventListener('click', () => this.hideModal());
        }

        // Close on Escape
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.modal && !this.modal.classList.contains('hidden')) {
                this.hideModal();
            }
        });
    }

    /**
     * Collect device information
     */
    collectDeviceInfo() {
        return {
            userAgent: navigator.userAgent,
            platform: navigator.platform || 'unknown',
            language: navigator.language,
            screenSize: `${screen.width}x${screen.height}`,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            pixelRatio: window.devicePixelRatio || 1,
            touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0
        };
    }

    /**
     * Collect current game state
     */
    collectGameState() {
        if (!this.game?.state) {
            return { error: 'Game state unavailable' };
        }

        const state = this.game.state;
        return {
            puzzleDate: state.puzzleDate,
            mode: state.mode,
            completedCategories: state.completedCategories || [],
            pairedWordsCount: state.pairedWords?.length || 0,
            currentCategoryId: state.deepDiveContext?.categoryId || null
        };
    }

    /**
     * Collect current settings
     */
    collectSettings() {
        const theme = document.documentElement.getAttribute('data-theme');
        return {
            darkMode: theme === 'dark',
            musicEnabled: !document.getElementById('music-toggle')?.classList.contains('muted'),
            sfxEnabled: !document.getElementById('sfx-toggle')?.classList.contains('muted')
        };
    }

    /**
     * Collect localStorage data for current puzzle
     */
    collectLocalStorage() {
        try {
            const puzzleDate = this.game?.state?.puzzleDate;
            if (!puzzleDate) return null;

            const key = `taxonomic_${puzzleDate}`;
            const data = localStorage.getItem(key);
            return data ? JSON.parse(data) : null;
        } catch (e) {
            return { error: 'Failed to read localStorage' };
        }
    }

    /**
     * Build complete bug report payload
     */
    buildPayload(userMessage) {
        return {
            userMessage,
            gameState: this.collectGameState(),
            deviceInfo: this.collectDeviceInfo(),
            settings: this.collectSettings(),
            consoleLogs: this.consoleCapture?.getEntries() || [],
            localStorage: this.collectLocalStorage(),
            meta: {
                timestamp: new Date().toISOString(),
                gameVersion: '1.0.0',
                url: window.location.href
            }
        };
    }

    /**
     * Submit bug report via Faro
     */
    async submit(userMessage) {
        const payload = this.buildPayload(userMessage);

        try {
            // Send bug report as error log (more visible in Faro UI than custom events)
            if (this.faro?.api?.pushLog) {
                this.faro.api.pushLog(
                    [`BUG REPORT: ${payload.userMessage}`],
                    {
                        level: 'error',
                        context: {
                            source: 'bug_report',
                            puzzleDate: payload.gameState.puzzleDate,
                            mode: payload.gameState.mode,
                            completedCategories: JSON.stringify(payload.gameState.completedCategories),
                            pairedWordsCount: String(payload.gameState.pairedWordsCount),
                            deviceInfo: JSON.stringify(payload.deviceInfo),
                            settings: JSON.stringify(payload.settings),
                            localStorage: JSON.stringify(payload.localStorage),
                            gameVersion: payload.meta.gameVersion,
                            url: payload.meta.url
                        }
                    }
                );
            }

            // Send console logs as separate log entries
            if (this.faro?.api?.pushLog && payload.consoleLogs.length > 0) {
                payload.consoleLogs.forEach(entry => {
                    this.faro.api.pushLog(
                        [`[console.${entry.level}] ${entry.message}`],
                        { level: 'info', context: { source: 'bug_report_console', originalTimestamp: entry.timestamp } }
                    );
                });
            }

            return true;
        } catch (error) {
            console.error('Failed to submit bug report:', error);
            return false;
        }
    }

    /**
     * Handle submit button click
     */
    async handleSubmit() {
        const message = this.messageInput?.value?.trim() || '';

        if (!message) {
            this.messageInput?.focus();
            return;
        }

        const submitBtn = document.getElementById('bug-report-submit');
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.textContent = 'Sending...';
        }

        await this.submit(message);

        this.showThankYou();

        // Auto-close after 2 seconds
        setTimeout(() => {
            this.hideModal();
            if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.textContent = 'Send Report';
            }
        }, 2000);
    }

    /**
     * Show the bug report modal
     */
    showModal() {
        if (!this.modal) return;

        // Clear previous input
        if (this.messageInput) {
            this.messageInput.value = '';
        }

        // Update technical details preview
        this.updateTechnicalDetails();

        // Hide feedback
        if (this.feedback) {
            this.feedback.classList.add('hidden');
        }

        this.modal.classList.remove('hidden');

        // Focus the textarea
        setTimeout(() => this.messageInput?.focus(), 100);

        // Announce to screen readers
        const announcer = document.getElementById('announcer');
        if (announcer) {
            announcer.textContent = 'Bug report dialog opened';
        }
    }

    /**
     * Hide the bug report modal
     */
    hideModal() {
        if (!this.modal) return;
        this.modal.classList.add('hidden');

        // Announce to screen readers
        const announcer = document.getElementById('announcer');
        if (announcer) {
            announcer.textContent = 'Bug report dialog closed';
        }
    }

    /**
     * Update the technical details preview
     */
    updateTechnicalDetails() {
        if (!this.technicalPre) return;

        const preview = {
            gameState: this.collectGameState(),
            deviceInfo: this.collectDeviceInfo(),
            settings: this.collectSettings(),
            consoleLogs: `${this.consoleCapture?.getEntries()?.length || 0} entries`,
            meta: {
                timestamp: new Date().toISOString(),
                gameVersion: '1.0.0'
            }
        };

        this.technicalPre.textContent = JSON.stringify(preview, null, 2);
    }

    /**
     * Show thank you message
     */
    showThankYou() {
        if (!this.feedback) return;
        this.feedback.textContent = '✓ Thank you! Report submitted.';
        this.feedback.classList.remove('hidden');
    }
}
